import debounce from 'lodash/debounce'
import getEnv from '../../utils/env'
import { publisherId } from '../../stv.config'

const enableConsoleLogging = false

// TODO: replace dfpState with Redux
export const dfpState = {
  scriptInjected: false,
  dfpInitialised: false,
  adSlots: {},
  set: (key, value) => { window.dfpState[key] = value },
  setSlot: (divId, slot) => { window.dfpState.adSlots[divId] = slot },
}

// eslint-disable-next-line consistent-return
export default function configureAds(params) {
  if (getEnv() === 'testing') return {}
  window.dfpState = window.dfpState || dfpState
  window.googletag = window.googletag || { cmd: [] }

  const { cmd } = window.googletag

  setupGlobalOnLoadHandler(params)

  injectScript()
  cmd.push(() => initDfp(params.options, params))
  cmd.push(() => configureSlot({
    slot: defineSlot(params),
    divId: params.divId,
  }))
}

export function setupGlobalOnLoadHandler({ onIframeLoad }) {
  if (Array.isArray(onIframeLoad)) {
    const [name, handler] = onIframeLoad
    window[name] = handler
      // eslint-disable-next-line no-console
      || (data => console.error('No onLoad handler defined', data))
  }
}

export function injectScript() {
  if (window.dfpState.scriptInjected) {
    cLog('* Ads * Script already on page')
    return
  }

  // Create gads script tag
  const gads = document.createElement('script')
  gads.id = 'g_dfp'
  gads.async = true
  gads.type = 'text/javascript'
  gads.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js'

  gads.addEventListener('load', () => cLog('* Ads * Script Loaded'))
  // eslint-disable-next-line no-console
  gads.addEventListener('error', e => console.error('* Ads * error loading script', e))

  // Insert tag before any existing script tags in document
  const node = document.getElementsByTagName('script')[0]
  node.parentNode.insertBefore(gads, node)

  window.dfpState.set('scriptInjected', true)
  cLog('* Ads * Script tag injected')
}

export function initDfp(options = {}, params) {
  if (window.dfpState.dfpInitialised) {
    return
  }

  const {
    disableInitialLoad = false,
    collapseEmptyDivs = true,
    setCentering = false,
  } = options

  const { googletag } = window

  // Required setup
  googletag.destroySlots()
  googletag.pubads().enableSingleRequest()

  // Optional
  if (disableInitialLoad) googletag.pubads().disableInitialLoad()
  if (collapseEmptyDivs) googletag.pubads().collapseEmptyDivs()
  if (setCentering) googletag.pubads().setCentering(true)

  // Environment
  const envLabel = getEnvLabel(getEnv())
  googletag.pubads().setTargeting('staging', [envLabel])
    .setTargeting('pr', [params.adSlotProps.title])
    .setTargeting('ca', [params.adSlotProps.programCategory])
    .setTargeting('cp', [params.adSlotProps.contentProvider])
    .setTargeting('cg', [params.adSlotProps.categoryAssoc])
  window.dfpState.set('dfpInitialised', true)
  cLog('* Ads * Initialised')
}

export function getEnvLabel(env) {
  switch (env) {
    case 'production': return 'no'
    case 'staging': return 'stvqa'
    default: return 'local'
  }
}

export function configureSlot({ slot, divId }) {
  window.dfpState.setSlot(divId, slot)
  cLog('* Ads * configured slot:', divId)
}

export function defineSlot({
  type,
  divId,
  path,
  programmeOrChannelGuid,
}) {
  try {
    const { googletag } = window
    const adUnitId = createAdUnitPath({ path })
    // const targetingKey = programmeOrChannelGuid ? 'pr' : 'ad'
    // const targetingValue = programmeOrChannelGuid || [type]

    switch (type) {
      case 'medium-rectangle': return googletag
        .defineSlot(adUnitId, [[300, 250]], divId)
        .defineSizeMapping(
          googletag.sizeMapping()
            .addSize([0, 0], [300, 250])
            .build()
        )
        .setTargeting('sp', ['medium-rectangle'])
        .setCollapseEmptyDiv(true, true)
        .addService(googletag.pubads())

      case 'sponsor-logo': return googletag
        .defineSlot(adUnitId, [[140, 50]], divId)
        .defineSizeMapping(
          googletag.sizeMapping()
            .addSize([0, 0], [])
            .addSize([140, 0], [140, 50])
            .build()
        )
        .setTargeting('sp', ['sponsor-logo'])
        .setCollapseEmptyDiv(true, true)
        .addService(googletag.pubads())

      case 'leaderboard':
        return googletag
          .defineSlot(adUnitId, [[728, 90]], divId)
          .defineSizeMapping(
            googletag.sizeMapping()
              .addSize([730, 0], [728, 90])
              .addSize([0, 0], [])
              .build()
          )
          .setTargeting('sp', ['leaderboard'])
          .setCollapseEmptyDiv(true, true)
          .addService(googletag.pubads())

        // create a native ad slot with fluid size mapping
      case 'native-regular':
        return googletag
          .defineSlot(adUnitId, 'fluid', divId)
          .defineSizeMapping(
            googletag.sizeMapping()
              .addSize([0, 0], 'fluid')
              .build()
          )
          .setTargeting('sp', ['native-regular'])
          .setCollapseEmptyDiv(true, true)
          .addService(googletag.pubads())

      case 'native-poster':
        return googletag
          .defineSlot(adUnitId, 'fluid', divId)
          .defineSizeMapping(
            googletag.sizeMapping()
              .addSize([0, 0], 'fluid')
              .build()
          )
          .setTargeting('sp', ['native-poster'])
          .setCollapseEmptyDiv(true, true)
          .addService(googletag.pubads())

      default: return null
    }
  } catch (error) {
    // stop this function throwing a massive stack track on every webpack hot-reload
    return null
  }
}

export function createAdUnitPath({ path }) {
  if (!path) return `${publisherId}/stv.tv_player`
  return `${publisherId}/stv.tv_player/${path}`
}

export function addResizeHandler(handler) {
  window.addEventListener('resize', debounce(handler, 500))
}

export function onWindowResize(divId) {
  return e => window.googletag.pubads().refresh(
    [window.dfpState.adSlots[divId]]
  )
}

export function refreshAds() {
  if (!window?.googletag?.pubads) return
  try {
    window.googletag.pubads().refresh(null, { changeCorrelator: false })
  } catch (err) {
    window.setTimeout(refreshAds, 1000)
    console.warn(err) // eslint-disable-line no-console
  }
}

export function showAllAds() {
  if (!window.googletag) {
    cLog('* Ads * showAllAds called before googletag was initialised')
    setTimeout(showAllAds, 500)
    return
  }
  window.googletag.cmd.push(() => {
    window.googletag.enableServices()
    Object.keys(window.dfpState.adSlots)
      .forEach(divId => window.googletag.display(divId))
  })
}

export function cLog(...args) {
  // eslint-disable-next-line no-console
  if (enableConsoleLogging) console.log(...args)
}
